import FocusTrap from '@pinecast/common/FocusTrap';
import styled from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import {useMountEffect} from './hooks/useMountEffect';
import type {LocalizedReactNode} from './types';

const WashContainer = styled('div', {
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  justifyContent: 'center',
  left: 0,
  position: 'fixed',
  top: 0,
  width: '100vw',
  zIndex: 100,
});
const WashInnerContainer = styled('div', {
  flex: '0 0 100%',
  maxHeight: '100%',
  overflowY: 'auto',
  padding: '15vh 20px',
  width: '100%',
  '@media (max-width: 700px)': {
    padding: '15vh 12px',
  },
});
export const Wash = ({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick?: () => void;
}) => {
  const ref = React.useRef<null | HTMLElement>(null);
  return (
    <WashContainer onClick={onClick}>
      <WashInnerContainer
        ref={ref}
        onClick={React.useCallback(
          (e: React.SyntheticEvent<HTMLDivElement>) => {
            e.stopPropagation();
            if (e.target === ref.current) {
              onClick?.();
            }
          },
          [onClick],
        )}
      >
        <FocusTrap>{children}</FocusTrap>
      </WashInnerContainer>
    </WashContainer>
  );
};

const Wrapper = styled(
  'div',
  ({$size}: {$size: 'small' | 'medium' | 'large'}, designColors) => ({
    backgroundColor: designColors.contentBackground,
    borderColor: designColors.contentBorder,
    borderStyle: 'solid',
    borderTopWidth: '1px',
    borderBottomWidth: '2px',
    borderLeftWidth: '1px',
    borderRightWidth: '1px',
    borderRadius: 6,
    boxShadow:
      'rgba(0, 0, 0, 0.2) 0px 3px 5px, rgba(0, 0, 0, 0.2) 0px 6px 10px',
    color: designColors.contentText,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: DEFAULT_FONT,
    margin: '0 auto',
    maxWidth: $size === 'small' ? 400 : $size === 'medium' ? 625 : 850,
    // minWidth: $size === 'small' ? 350 : $size === 'medium' ? 550 : 650,
    overflow: 'hidden',
    textAlign: 'left',
    width: '100%',
  }),
);
const Header = styled('header', (_, designColors) => ({
  alignItems: 'center',
  borderBottom: `1px solid ${designColors.contentBorder}`,
  display: 'flex',
  fontSize: 15,
  fontFamily: 'Monument Grotesk Bold',
  height: 46,
  padding: '0 15px',
}));
const Body = styled(
  'section',
  ({$size}: {$size: 'small' | 'medium' | 'large'}, designColors) => ({
    // backgroundColor: designColors.background,
    fontSize: 13,
    minHeight: $size === 'small' ? 80 : $size === 'medium' ? 120 : 240,
    padding: 15,
  }),
);
const Footer = styled('footer', (_, designColors) => ({
  alignItems: 'center',
  backgroundColor: designColors.contentBackground,
  display: 'flex',
  height: 46,
  justifyContent: 'flex-end',
  padding: '0 15px',
  textAlign: 'right',
}));

type Props = {
  actions?: React.ReactNode;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  title: LocalizedReactNode;
};

const Dialog = ({actions, children, size, title}: Props) => {
  const id = React.useId();
  const actionWrapperRef = React.useRef<HTMLElement | null>(null);

  useMountEffect(() => {
    setTimeout(() => {
      (
        actionWrapperRef.current?.querySelector(
          '[data-autofocus]',
        ) as HTMLElement | null
      )?.focus();
    }, 0);
  });

  let actionWrapper: JSX.Element | null = null;
  if (actions) {
    actionWrapper = <Footer ref={actionWrapperRef}>{actions}</Footer>;
  }

  return (
    <Wrapper
      aria-modal
      role="dialog"
      $size={size || 'medium'}
      aria-labelledby={`${id}__title`}
      aria-describedby={`${id}__body`}
    >
      <Header id={`${id}__title`}>{title}</Header>
      <Body id={`${id}__body`} $size={size || 'medium'}>
        {children}
      </Body>
      {actionWrapper}
    </Wrapper>
  );
};

export default React.memo(Dialog);
